import apiClient from "../api/apiClient";
import { clearLoginSession } from "../components/LoginProviders";
import { extractRoles, extractScopes } from "./authStorage";
import { resolvePostLoginPath } from "./navigationScopes";
import {
  getGoogleOAuthRedirectUri,
  getMicrosoftOAuthRedirectUri,
} from "./oauthRedirectUris";

type CompleteOAuthLoginParams = {
  provider: "google" | "microsoft";
  code: string;
  login: (accessToken: string, refreshToken?: string) => Promise<void>;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  fromPath?: string;
};

export async function completeOAuthLogin({
  provider,
  code,
  login,
  navigate,
  fromPath = "/",
}: CompleteOAuthLoginParams): Promise<void> {
  const redirectUri =
    provider === "microsoft"
      ? getMicrosoftOAuthRedirectUri()
      : getGoogleOAuthRedirectUri();

  const inviteToken = sessionStorage.getItem("invite_token");
  const inviteOrgId = sessionStorage.getItem("invite_org_id");
  const microsoftContextToken = sessionStorage.getItem(
    "microsoft_oauth_context_token",
  );

  const response = await apiClient.post(`/auth/${provider}/callback`, {
    code,
    redirect_uri: redirectUri,
    invite_token: inviteToken || undefined,
    org_id: inviteOrgId ? Number(inviteOrgId) : undefined,
    context_token: microsoftContextToken || undefined,
  });

  const { access_token, refresh_token } = response.data;

  sessionStorage.removeItem("invite_token");
  sessionStorage.removeItem("invite_org_id");
  clearLoginSession();
  sessionStorage.removeItem("oauth_provider");

  await login(access_token, refresh_token);

  setTimeout(async () => {
    try {
      if (provider === "google") {
        await apiClient.post("/gmail/watch");
      } else if (provider === "microsoft") {
        await apiClient.post("/outlook/watch");
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(
          `${provider} watch setup failed (non-blocking):`,
          error,
        );
      }
    }
  }, 500);

  const freshPayload = JSON.parse(
    atob(access_token.split(".")[1]),
  ) as Record<string, unknown>;
  if (freshPayload.mfa === "pending") {
    navigate("/mfa", { replace: true });
    return;
  }

  try {
    const { data } = await apiClient.get<{ onboarding_required: boolean }>(
      "/user/onboarding/status",
    );
    if (data?.onboarding_required) {
      navigate("/onboarding", { replace: true });
      return;
    }
  } catch {
    // Preserve login navigation if onboarding status is transiently unavailable.
  }

  const resolvedScopes = extractScopes(freshPayload);
  const resolvedRoles = extractRoles(freshPayload);
  const canAdmin = resolvedRoles.some((r) => r === "ADMIN");
  navigate(
    resolvePostLoginPath(fromPath, resolvedScopes, {
      canAccessAdminRoute: canAdmin,
    }),
    { replace: true },
  );
}
